import i18n from 'i18next';

/**
 * Get the current language code from i18n
 * @returns {string} Current language code (e.g., 'en' or 'de')
 */
export const getCurrentLanguage = () => {
  const lang = i18n.language || 'en';
  return lang.split('-')[0]; // Handle 'en-US' format
};

/**
 * Add language prefix to a path
 * English doesn't get a prefix (default), other languages do (e.g., /de/)
 * @param {string} path - The path to prefix with language (e.g., '/contact')
 * @param {string} lang - Optional language code (defaults to current language)
 * @returns {string} Path with language prefix if needed (e.g., '/de/contact' or '/contact')
 */
export const getLocalizedPath = (path, lang = null) => {
  const language = lang || getCurrentLanguage();

  // English is default, no prefix needed
  if (language === 'en') {
    // Remove any language prefix if present
    return path.replace(/^\/(de)(\/|$)/, '/');
  }

  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;

  // Check if path already has the correct language prefix
  if (cleanPath.startsWith(`${language}/`)) {
    return `/${cleanPath}`;
  }

  // Remove any other language prefix
  const pathWithoutLang = cleanPath.replace(/^(de)(\/|$)/, '');

  // Special case for root path
  if (!pathWithoutLang || pathWithoutLang === '') {
    return `/${language}`;
  }

  return `/${language}/${pathWithoutLang}`;
};

/**
 * Remove language prefix from a path
 * @param {string} path - The path with language prefix (e.g., '/de/contact')
 * @returns {string} Path without language prefix (e.g., '/contact')
 */
export const removeLanguagePrefix = path => {
  return path.replace(/^\/(de)(\/|$)/, '/');
};

/**
 * Get alternate language links for a given path (for SEO)
 * @param {string} path - The current path
 * @returns {Array} Array of objects with lang and href
 */
export const getAlternateLanguageLinks = path => {
  const languages = ['en', 'de'];
  const currentLang = getCurrentLanguage();
  const cleanPath = removeLanguagePrefix(path);

  return languages
    .filter(lang => lang !== currentLang)
    .map(lang => ({
      lang,
      href: getLocalizedPath(cleanPath, lang),
    }));
};
